import { useState, useEffect } from 'react'
import { useStudentCalendar } from '@/hooks/useStudentCalendar'
import { useBookableSlots } from '@/hooks/useBookableSlots'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { MonthGrid, type CalendarEvent } from '@/components/calendar/MonthGrid'
import { LessonCard, LessonCardButton } from '@/components/ui/LessonCard'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-CA', {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}
function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-CA', { hour: 'numeric', minute: '2-digit' })
}
function toDateKey(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const sectionStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14,
  padding: '20px 22px', marginBottom: 18,
}
const sectionTitleStyle: React.CSSProperties = {
  font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 14,
}

const REQUEST_ERROR_MESSAGES: Record<string, string> = {
  unauthorized: 'You are not authorized to request this lesson.',
  invalid_time_range: 'Pick a valid start and finish time.',
  invalid_duration: 'A lesson cannot be longer than 8 hours.',
  relationship_not_found: 'That instructor relationship could not be found.',
  relationship_not_active: 'That instructor relationship is no longer active.',
  slot_conflict: 'That time is no longer available.',
  outside_availability: 'That time is outside your instructor\'s available hours.',
  reschedule_already_pending: 'You already have a reschedule request pending for this lesson.',
}

interface MyInstructorRelationship {
  relationshipId: string
  instructorId: string
  instructorName: string | null
}

function useMyActiveInstructors() {
  const { user } = useAuth()
  const [relationships, setRelationships] = useState<MyInstructorRelationship[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) { setLoading(false); return }
    const uid = user.id
    let cancelled = false
    async function load() {
      const { data: rels } = await supabase
        .from('instructor_student_relationships')
        .select('id, instructor_id')
        .eq('student_id', uid)
        .eq('status', 'active')
      if (cancelled) return
      const instructorIds = [...new Set((rels ?? []).map(r => r.instructor_id))]
      const { data: profiles } = instructorIds.length
        ? await supabase.from('profiles').select('id, full_name').in('id', instructorIds)
        : { data: [] }
      if (cancelled) return
      const nameById = new Map((profiles ?? []).map(p => [p.id, p.full_name]))
      setRelationships((rels ?? []).map(r => ({
        relationshipId: r.id, instructorId: r.instructor_id, instructorName: nameById.get(r.instructor_id) ?? null,
      })))
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [user])

  return { relationships, loading }
}

const DURATION_OPTIONS = [30, 45, 60, 90, 120]

function DurationSelect({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Lesson length</div>
      <select value={value} onChange={e => onChange(Number(e.target.value))} className='field-input'>
        {DURATION_OPTIONS.map(m => (
          <option key={m} value={m}>{m < 60 ? `${m} min` : m === 60 ? '1 hour' : `${(m / 60).toFixed(1).replace('.0', '')} hours`}</option>
        ))}
      </select>
    </div>
  )
}

// ── Free-form date/time/duration picker with a live availability check ──────
//
// Replaces the old "pick one of these pre-stepped slots" list: a rigid grid couldn't
// offer every time a student might actually want (11:15-12:15 was reported as literally
// impossible to ask for). The backend's own availability+conflict check
// (is_within_instructor_availability(), migration 094) remains the real gatekeeper either
// way -- this only changes what the student sees before submitting, from a fixed list to
// a free date+time+duration entry with a live, non-blocking "is this actually open"
// check, so they're not guessing blind but also aren't limited to someone else's grid.

function todayDateString() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function dateStringPlusDays(days: number) {
  const d = new Date(Date.now() + days * 24 * 60 * 60 * 1000)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// freeStartTimes holds every free 15-minute-aligned start (as epoch ms, from a
// p_duration_minutes=15 fetch -- migration 099 steps every 15 minutes, matching this
// check's own resolution). A chosen [start, start+duration) range is "free" only if
// EVERY 15-minute block it spans is itself a free start -- not just the first one --
// so a range that overlaps a booking anywhere in its middle is correctly flagged.
function isRangeFree(freeStartTimes: Set<number>, startMs: number, durationMinutes: number): boolean {
  const steps = durationMinutes / 15
  for (let i = 0; i < steps; i++) {
    if (!freeStartTimes.has(startMs + i * 15 * 60 * 1000)) return false
  }
  return true
}

function LessonTimePicker({
  relationshipId, instructorName, durationMinutes, onDurationChange, onRangeChange,
}: {
  relationshipId: string
  instructorName: string
  durationMinutes: number
  onDurationChange: (m: number) => void
  onRangeChange: (range: { startAt: string; endAt: string } | null) => void
}) {
  const [date, setDate] = useState(() => dateStringPlusDays(1))
  const [time, setTime] = useState('11:00')
  const minDate = todayDateString()
  const maxDate = dateStringPlusDays(59)

  // A 15-minute-duration fetch for just this one day gives every free quarter-hour start
  // -- the finest resolution get_bookable_slots offers, and the only one this check needs.
  const { slots: dayFreeSlots, loading: checking } = useBookableSlots(relationshipId, date, date, 15)
  const freeStartTimes = new Set(dayFreeSlots.map(s => new Date(s.startAt).getTime()))

  const start = date && time ? new Date(`${date}T${time}`) : null
  const range = start && !isNaN(start.getTime())
    ? { startAt: start.toISOString(), endAt: new Date(start.getTime() + durationMinutes * 60 * 1000).toISOString() }
    : null

  useEffect(() => { onRangeChange(range) }, [range?.startAt, range?.endAt])

  const available = range ? isRangeFree(freeStartTimes, start!.getTime(), durationMinutes) : null

  return (
    <>
      <div className='fg2'>
        <div>
          <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Date</div>
          <input type='date' value={date} min={minDate} max={maxDate} onChange={e => setDate(e.target.value)} className='field-input' />
        </div>
        <div>
          <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Start time</div>
          <input type='time' value={time} step={900} onChange={e => setTime(e.target.value)} className='field-input' />
        </div>
      </div>
      <DurationSelect value={durationMinutes} onChange={onDurationChange} />
      {range && (
        <div style={{
          font: '500 12.5px Inter, sans-serif',
          color: checking ? 'rgba(27,42,74,0.45)' : available ? '#0F7C6C' : '#9A6C0E',
        }}>
          {checking
            ? 'Checking availability…'
            : available
              ? `✓ Within ${instructorName}'s available hours`
              : `⚠ Outside ${instructorName}'s available hours, or already booked — they can still review and decline or adjust this request`}
        </div>
      )}
    </>
  )
}

function RequestLessonForm({ onRequested }: { onRequested: () => void }) {
  const { relationships } = useMyActiveInstructors()
  const { requestLesson, saving } = useStudentCalendar()
  const [open, setOpen] = useState(false)
  const [relationshipId, setRelationshipId] = useState('')
  const [durationMinutes, setDurationMinutes] = useState(60)
  const [range, setRange] = useState<{ startAt: string; endAt: string } | null>(null)
  const [error, setError] = useState('')

  const instructorName = relationships.find(r => r.relationshipId === relationshipId)?.instructorName ?? 'your instructor'

  const handleSubmit = async () => {
    setError('')
    if (!relationshipId) { setError('Pick an instructor.'); return }
    if (!range) { setError('Pick a date and time.'); return }
    const result = await requestLesson({ relationshipId, startAt: range.startAt, endAt: range.endAt })
    if (!result.ok) { setError(REQUEST_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not send this request.'); return }
    setOpen(false)
    setRelationshipId('')
    setRange(null)
    onRequested()
  }

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 16 : 0 }}>
        <div style={sectionTitleStyle}>Request a lesson</div>
        <button onClick={() => setOpen(v => !v)} className='btn-teal' style={{ width: 'auto', padding: '9px 16px' }}>
          {open ? 'Cancel' : '+ Request a lesson'}
        </button>
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Instructor</div>
            <select value={relationshipId} onChange={e => { setRelationshipId(e.target.value); setRange(null) }} className='field-input' disabled={saving}>
              <option value=''>Select an instructor…</option>
              {relationships.map(r => <option key={r.relationshipId} value={r.relationshipId}>{r.instructorName ?? 'Instructor'}</option>)}
            </select>
          </div>

          {relationshipId && (
            <LessonTimePicker
              relationshipId={relationshipId}
              instructorName={instructorName}
              durationMinutes={durationMinutes}
              onDurationChange={setDurationMinutes}
              onRangeChange={setRange}
            />
          )}

          {error && <div className='banner-error'>{error}</div>}
          <button className='btn-teal' onClick={handleSubmit} disabled={saving} style={{ width: 'auto', padding: '11px 22px', alignSelf: 'flex-start' }}>
            {saving ? 'Sending…' : 'Send request'}
          </button>
        </div>
      )}
    </div>
  )
}

function RescheduleRequestForm({ lessonId, relationshipId, instructorName, onDone }: { lessonId: string; relationshipId: string; instructorName: string; onDone: () => void }) {
  const { requestReschedule, saving } = useStudentCalendar()
  const [durationMinutes, setDurationMinutes] = useState(60)
  const [range, setRange] = useState<{ startAt: string; endAt: string } | null>(null)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    setError('')
    if (!range) { setError('Pick a new date and time.'); return }
    const result = await requestReschedule(lessonId, range.startAt, range.endAt)
    if (!result.ok) { setError(REQUEST_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not send this request.'); return }
    onDone()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10, padding: 12, background: 'rgba(27,42,74,0.03)', borderRadius: 10 }}>
      <LessonTimePicker
        relationshipId={relationshipId}
        instructorName={instructorName}
        durationMinutes={durationMinutes}
        onDurationChange={setDurationMinutes}
        onRangeChange={setRange}
      />
      {error && <div className='banner-error'>{error}</div>}
      <button className='btn-teal' onClick={handleSubmit} disabled={saving} style={{ width: 'auto', padding: '8px 16px', alignSelf: 'flex-start' }}>
        {saving ? 'Sending…' : 'Send reschedule request'}
      </button>
    </div>
  )
}

export default function StudentCalendar() {
  const { lessons, requests, loading, error, saving, cancelLesson, withdrawRequest, refresh } = useStudentCalendar()
  const [reschedulingId, setReschedulingId] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null)

  const events: CalendarEvent[] = [
    ...lessons.map(l => ({
      id: l.id, date: toDateKey(l.scheduledAt), label: l.instructorName ?? 'Lesson', color: '#0F7C6C',
    })),
    ...requests.map(r => ({
      id: r.id, date: toDateKey(r.requestedStart), label: `? ${r.instructorName ?? 'Request'}`, color: '#D4972B',
    })),
  ]

  const visibleLessons = selectedDate ? lessons.filter(l => toDateKey(l.scheduledAt) === selectedDate) : lessons
  const visibleRequests = selectedDate ? requests.filter(r => toDateKey(r.requestedStart) === selectedDate) : requests

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 20 }}>Calendar</div>

      <div className='cal-layout'>
      <div className='cal-col-left'>
      <MonthGrid events={events} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      {selectedDate && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
            Showing {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <button onClick={() => setSelectedDate(null)} style={{ background: 'none', border: 'none', color: '#0F7C6C', cursor: 'pointer', font: '500 12.5px Inter, sans-serif' }}>
            Show all
          </button>
        </div>
      )}
      </div>

      <div className='cal-col-right'>
      <RequestLessonForm onRequested={refresh} />

      {error && <div className='banner-error' style={{ marginBottom: 16 }}>{error}</div>}

      {visibleRequests.length > 0 && (
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Your pending requests</div>
          {visibleRequests.map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
              <div>
                <div style={{ font: '500 14px Inter, sans-serif', color: '#1B2A4A' }}>
                  {r.kind === 'reschedule' ? 'Reschedule request' : 'Lesson request'} — {r.instructorName ?? 'Instructor'}
                </div>
                <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
                  {formatDateTime(r.requestedStart)} – {formatTime(r.requestedEnd)} · awaiting confirmation
                </div>
              </div>
              <button
                onClick={() => withdrawRequest(r.id)}
                disabled={saving}
                style={{ padding: '7px 14px', background: 'transparent', border: '1px solid rgba(27,42,74,0.15)', borderRadius: 8, color: '#1B2A4A', cursor: 'pointer', font: '500 12.5px Inter, sans-serif', flexShrink: 0 }}
              >
                Withdraw
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Upcoming lessons</div>
        {loading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>}
        {!loading && visibleLessons.length === 0 && (
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>{selectedDate ? 'No lessons on this date.' : 'No upcoming lessons booked.'}</div>
        )}
        {!loading && visibleLessons.map(l => (
          <LessonCard
            key={l.id}
            title={l.instructorName ?? 'Instructor'}
            meta={`${formatDateTime(l.scheduledAt)}${l.plannedEndAt ? ` – ${formatTime(l.plannedEndAt)}` : ''}`}
          >
            <LessonCardButton label='Reschedule' onClick={() => setReschedulingId(id => id === l.id ? null : l.id)} />
            <LessonCardButton label='Cancel' tone='danger' disabled={saving} onClick={() => setConfirmCancelId(l.id)} />
            {reschedulingId === l.id && (
              <div style={{ flexBasis: '100%' }}>
                <RescheduleRequestForm
                  lessonId={l.id}
                  relationshipId={l.relationshipId}
                  instructorName={l.instructorName ?? 'your instructor'}
                  onDone={() => { setReschedulingId(null); refresh() }}
                />
              </div>
            )}
          </LessonCard>
        ))}
      </div>
      </div>
      </div>

      <ConfirmDialog
        open={!!confirmCancelId}
        title='Cancel this lesson?'
        message="This will cancel the lesson and let your instructor know. This can't be undone — you'd need to request a new time."
        onCancel={() => setConfirmCancelId(null)}
        onConfirm={() => { cancelLesson(confirmCancelId!); setConfirmCancelId(null) }}
      />
    </div>
  )
}
